/**
 * Sticky top header — lockup TOM | EXPOGREENS + controles del usuario.
 * Diseño "Expogreens con TOM" (handoff de Claude Design).
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, LogOut, Maximize2, Minimize2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { TomMark } from '@/components/ui/TomMark'

/** Iniciales (máx. 2) para el avatar del usuario. */
function initialsOf(name = '') {
  return (
    name
      .split(' ')
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U'
  )
}

export function Header() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  useEffect(() => {
    const sync = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await document.documentElement.requestFullscreen()
    } catch {
      /* requiere gesto del usuario / https */
    }
  }

  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <header className="bg-expo-dark text-white sticky top-0 z-50">
      <div className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 flex items-center justify-between gap-3 min-w-0">
        {/* lockup */}
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 sm:gap-4">
            <span className="sm:hidden"><TomMark fontSize={24} dot="#C3DB5A" /></span>
            <span className="hidden sm:inline-flex"><TomMark fontSize={30} dot="#C3DB5A" /></span>
            <span className="w-px h-6 sm:h-7 bg-white/30" />
            <span
              style={{ fontFamily: 'var(--font-display)', color: '#FFFFFF', letterSpacing: '.01em' }}
              className="font-bold text-[20px] sm:text-[27px] leading-none truncate"
            >
              EXPOGREENS
            </span>
          </div>
          <p className="hidden sm:block text-[12px] mt-0.5 pl-0.5" style={{ color: '#C3DB5A' }}>
            Sistema de Gestión de Exportación de Follajes
          </p>
        </div>

        {user ? (
          <div className="relative flex-none" ref={menuRef}>
            {/* Botón de usuario (sin padding) que abre el menú de sesión */}
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              title={user.name || user.username}
              className="flex items-center gap-2 rounded-full p-0 hover:opacity-90 transition-opacity"
            >
              <span
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-none font-bold text-[14px] sm:text-[15px]"
                style={{ background: '#C3DB5A', color: '#033021' }}
              >
                {initialsOf(user.name || user.username)}
              </span>
              <span style={{ fontFamily: 'Whyte, sans-serif' }} className="hidden sm:block text-[15px] font-semibold text-white">
                {user.name || user.username}
              </span>
              <ChevronDown
                size={16}
                className="hidden sm:block text-white/70 transition-transform"
                style={{ transform: menuOpen ? 'rotate(180deg)' : 'none' }}
              />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[180px] rounded-2xl bg-white py-1.5"
                style={{ border: '1px solid #E6EFE3', boxShadow: '0 16px 40px rgba(20,80,42,0.18)' }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    toggleFullscreen()
                  }}
                  style={{ fontFamily: 'Whyte, sans-serif' }}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-[14px] font-semibold text-[#15321F] hover:bg-[#F6FAEF] transition-colors"
                >
                  {isFullscreen ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
                  {isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                </button>
                <div className="my-1 h-px bg-[#EAF0E6]" />
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    onLogout()
                  }}
                  style={{ fontFamily: 'Whyte, sans-serif' }}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-[14px] font-semibold text-[#C0392B] hover:bg-[#FBEEEC] transition-colors"
                >
                  <LogOut size={17} />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </header>
  )
}
